import React, { useCallback, useContext, useMemo, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  Platform,
  View,
  ViewProps,
} from "react-native";
import styled, { ThemeContext } from "styled-components/native";
import Icon from "../../icon";
import { QKIcon } from "../icon";
import { uuid } from "@app/util/uuid";
import { Image } from "expo-image";
import * as VideoThumbnails from "expo-video-thumbnails";
import Text from "@app/components/questkit/text";
import ConditionalWrap from "conditional-wrap";
import { UploadWrapperWeb } from "../uploadWrapper.web";
import * as ImagePicker from "expo-image-picker";
import { useActionSheet } from "@expo/react-native-action-sheet";
import BasePressable, {
  PressEvent,
} from "@app/components/questkit/BasePressable";
import { createLink } from "@app/util/link.utils";
import { Analytics } from "@app/analytics";
import isEqual from "react-fast-compare";
import PressableOpacity from "@app/components/questkit/PressableOpacity";
import { MediaSliderState, useFileIds, useFile } from "./MediaSliderState";
import {
  QMFile,
  fileIsLoading,
  SavedFile,
  QueuedFile,
  UploadFile,
  fileIsSaved,
  fileIsError,
} from "@app/components/questkit/MediaSliderV2/MediaSliderTypes";
import { MediaItemType } from "@questmate/common";
import {
  MobileCameraMediaItem,
  MobileGalleryMediaItem,
  WebMediaItem,
} from "@app/types/mediaPicker";

interface MediaSliderViewProps {
  editMode: boolean;
  readOnly: boolean;
  accessibilityLabelledBy?: ViewProps["accessibilityLabelledBy"];
  state: MediaSliderState;
}

interface MediaItemViewProps {
  editMode: boolean;
  readOnly: boolean;
  fileId: string;
  state: MediaSliderState;
}

const captureFromCamera = async (): Promise<MobileCameraMediaItem[]> => {
  const { status } = await ImagePicker.requestCameraPermissionsAsync();
  if (status !== "granted") {
    return [];
  }
  const result = await ImagePicker.launchCameraAsync({
    mediaTypes: ["images", "videos", "livePhotos"],
    quality: 1,
    allowsMultipleSelection: true,
    exif: false,
  });

  if (result.canceled) {
    return [];
  } else {
    return result.assets.map((file) => {
      const name = file.uri.split("/").pop();
      const extension =
        name && name.split(".").length > 1 ? name.split(".").pop() || "" : "";
      return {
        uri: file.uri,
        name: `capture.${extension.toLowerCase()}`,
        type: file.type,
      };
    });
  }
};

const selectFromGallery = async (): Promise<MobileGalleryMediaItem[]> => {
  const result = await ImagePicker.launchImageLibraryAsync({
    mediaTypes: ["images", "videos", "livePhotos"],
    quality: 1,
    allowsMultipleSelection: true,
  });
  if (result.canceled) {
    return [];
  } else {
    return result.assets.map((file) => {
      const name = file.uri.split("/").pop();
      const extension =
        name && name.split(".").length > 1 ? name.split(".").pop() || "" : "";

      return {
        uri: file.uri,
        name: file.fileName || `upload.${extension.toLowerCase()}`,
        type: file.type,
      };
    });
  }
};

export const MediaSliderV2: React.FC<MediaSliderViewProps> = ({
  editMode,
  readOnly,
  accessibilityLabelledBy,
  state,
}) => {
  const { showActionSheetWithOptions } = useActionSheet();
  const [mediaSliderId] = useState(uuid());

  const fileIds = useFileIds(state);

  const uploadFilesWeb = useCallback(
    (files: WebMediaItem[]) => {
      if (files.length > 0) {
        state.uploadFiles(
          files.map(({ blob }) => ({
            localPreviewUri: URL.createObjectURL(blob),
            file: blob,
          }))
        );
      }
    },
    [state]
  );
  const uploadFilesNative = useCallback(
    async (files: MobileCameraMediaItem[] | MobileGalleryMediaItem[]) => {
      if (files.length > 0) {
        const filesWithPreviews = await Promise.all(
          files.map(async (file) => {
            let localPreviewUri: string | undefined;
            if (file.type === MediaItemType.Image) {
              localPreviewUri = file.uri?.startsWith("/")
                ? `file://${file.uri}`
                : file.uri;
            } else if (file.type === MediaItemType.Video) {
              const videoPreview = await VideoThumbnails.getThumbnailAsync(
                file.uri,
                {
                  quality: 1,
                  time: 0,
                }
              );
              // todo: determine if the videoPreview.uri ever starts with a slash
              localPreviewUri = videoPreview.uri?.startsWith("/")
                ? `file://${videoPreview.uri}`
                : videoPreview.uri;
            }

            return {
              localPreviewUri,
              file,
            };
          })
        );
        // console.info("Preparing to upload files", filesWithPreviews);
        state.uploadFiles(filesWithPreviews);
      }
    },
    [state]
  );

  return (
    <View onStartShouldSetResponder={() => true}>
      <ConditionalWrap
        condition={Platform.OS === "web" && !readOnly}
        wrap={(children) => (
          <UploadWrapperWeb
            uploadFiles={uploadFilesWeb}
            uploadButtonId={`hidden-upload-button-web-${mediaSliderId}`}
          >
            {children}
          </UploadWrapperWeb>
        )}
      >
        <FlatList
          showsHorizontalScrollIndicator={false}
          horizontal={true}
          accessibilityLabelledBy={accessibilityLabelledBy}
          ListHeaderComponent={
            Platform.OS === "web" ? (
              <label htmlFor={`hidden-upload-button-web-${mediaSliderId}`}>
                <AddMediaItem editMode={editMode}>
                  <Icon icon={"plus"} size={32} inverted />
                </AddMediaItem>
              </label>
            ) : (
              <PressableOpacity
                disabled={readOnly}
                accessibilityRole="button"
                accessibilityHint="Add media"
                onPress={async () => {
                  // Open picker
                  showActionSheetWithOptions(
                    {
                      options: ["Take photo", "Select from gallery", "Cancel"],
                      destructiveButtonIndex: 2,
                    },
                    async (selectedIndex: number) => {
                      switch (selectedIndex) {
                        case 0:
                          // Camera
                          void uploadFilesNative(await captureFromCamera());
                          break;
                        case 1:
                          // Gallery
                          void uploadFilesNative(await selectFromGallery());
                          break;
                        case 2:
                        // Canceled
                      }
                    }
                  );
                }}
              >
                <AddMediaItem editMode={editMode}>
                  <Icon icon={"plus"} size={32} inverted />
                </AddMediaItem>
              </PressableOpacity>
            )
          }
          data={fileIds}
          keyExtractor={(_item, index) => index.toString()}
          renderItem={({ item, index }) => {
            return (
              <MediaItemView
                editMode={editMode}
                readOnly={readOnly}
                fileId={item}
                state={state}
                // onPressDelete={() => onDeleteMediaItem(index)}
                key={"images" + index}
              />
            );
          }}
        />
      </ConditionalWrap>
    </View>
  );
};

function isInProgress(mediaItem: QMFile): mediaItem is QueuedFile | UploadFile {
  return fileIsLoading(mediaItem);
}

function isSaved(mediaItem: QMFile): mediaItem is SavedFile {
  return fileIsSaved(mediaItem);
}

const MediaItemView: React.FC<MediaItemViewProps> = React.memo(
  ({ editMode, fileId, state, readOnly }) => {
    const themeContext = useContext(ThemeContext);

    const file = useFile(state, fileId);

    const onPress = useMemo(() => {
      if (!file || !isSaved(file)) {
        return;
      }
      return createLink(file.signedUrl, {
        newTabOnWeb: true,
        onPressHook: () => {
          Analytics.trackEvent("Open Media Item", { type: file.type });
        },
      });
    }, [file]);

    if (!file) {
      return null;
    }
    return (
      <MediaItemWrapper onPress={onPress} disabled={!onPress}>
        {/* Thumbnail */}
        {file.src && (
          <ThumbnailImage
            source={{ uri: file.src }}
            blurImage={!isSaved(file)}
          />
        )}
        {file.name && (
          <FilenameText size="small" numberOfLines={2}>
            {file.name}
          </FilenameText>
        )}
        {/* Progress indicator */}
        {isInProgress(file) ? (
          <UploadProgressIndicator>
            <ActivityIndicator color={themeContext.background} size="small" />
          </UploadProgressIndicator>
        ) : null}
        {editMode || !readOnly ? (
          <FileActions>
            {/* Retry button */}
            {fileIsError(file) ? (
              <FileActionButton
                onPress={(event: PressEvent) => {
                  event.preventDefault();
                  state.retryUpload(file.id);
                }}
              >
                <QKIcon name="refresh" />
              </FileActionButton>
            ) : null}
            {/* Delete action */}
            <FileActionButton
              onPress={(event: PressEvent) => {
                event.preventDefault();
                state.removeFile(file.id);
              }}
            >
              <QKIcon name="trash" />
            </FileActionButton>
          </FileActions>
        ) : null}
      </MediaItemWrapper>
    );
  },
  isEqual
);
MediaItemView.displayName = "MediaItemView";

const AddMediaItem = styled.View<{ editMode?: boolean }>`
  ${({ theme, editMode }) => `
      height: 120px;
      width: 120px;
      margin-right: 16px;
      border-radius: 8px;
      justify-content: center;
      align-items: center;
      background-color: ${
        editMode ? theme.interaction.neutralStrong : theme.interaction.primary
      };
      ${Platform.OS === "web" && !editMode && "cursor: pointer;"}
    `}
`;

const FilenameText = styled(Text)`
  position: absolute;
  width: 120px;
  padding-left: 7px;
  padding-right: 7px;
  padding-top: 4px;
  padding-bottom: 4px;
  top: 0;
  left: 0;
  color: ${({ theme }) => theme.interaction.secondary};
  background-color: ${({ theme }) => theme.interaction.primary};
  opacity: 0.8;
`;

const FileActions = styled.View`
  position: absolute;
  right: 7px;
  bottom: 7px;
  flex-direction: row;
`;

const FileActionButton = styled(BasePressable)`
  width: 30px;
  height: 30px;
  margin-left: 7px;
  border-radius: 15px;
  justify-content: center;
  align-items: center;
  overflow: hidden;
`;

interface ThumbnailImageProps {
  blurImage: boolean;
}
const ThumbnailImage = styled(Image)<ThumbnailImageProps>`
  width: 120px;
  height: 120px;
  opacity: ${({ blurImage }) => (blurImage ? 0.2 : 1)};
`;

const MediaItemWrapper = styled(BasePressable)`
  overflow: hidden;
  border-radius: 8px;
  width: 120px;
  height: 120px;
  margin-right: 16px;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.interaction.primary};
`;

const UploadProgressIndicator = styled.View`
  background-color: ${({ theme }) => theme.interaction.primary};
  position: absolute;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  border-radius: 20px;
`;
